/**
 * The Technical Delegate's own quick-note capture — `01-command-desk.md` §12.
 *
 * **Why the writing surface is here and not on the profile.** Spec 04 §B7 is explicit that the
 * Official Profile is where quick notes are READ: *"There is no add-note or dictate button on
 * this page — the profile is where notes are read, not written. This is deliberate and should
 * be preserved."* It also says where they come from: the pitch-side capture companion, spec 01
 * §12, which is this. So the profile panel lists them and this card writes them, and neither
 * grows the other's job.
 *
 * §12's three steps, in its own words: *"01 Select the official from the tournament roster · 02
 * Choose a grading category — role-specific · 03 Type it, or use your keyboard's mic; the note
 * files itself to the desktop workbench."*
 *
 * **Step 02 is built as of run 42.** The picker offers the selected official's OWN marking set
 * — `tdNoteCategoriesForRole`, the same fixed FIH criteria the marking panel draws — plus
 * `General`, which is the default. The category lands in `td_note.criterion_stable_key`, the
 * column migration 037 parked for exactly this, so a note filed at pitch-side and a mark
 * entered on the reporting screen name the same criterion without translation.
 *
 * **`General` is stored as null, and that is the whole storage decision.** Migration 037 wrote
 * the column nullable and read an unwritten key as a note *"read back uncategorised"*; General
 * is that absence rather than a sentinel string, so every note captured before this picker
 * existed is already a General note and no backfill was needed. General notes are also the ones
 * the draft tournament report summary will read — a note the TD did not file against a
 * criterion is a note about the official at large — which the field's help copy says in words.
 *
 * **Changing the official resets the category to General**, because the sets are role-specific:
 * `Flow & Timing` is an Umpire criterion and means nothing on a Technical Officer, and a
 * category carried silently across a change of subject files evidence under a criterion the
 * person is not marked on for the whole tournament.
 *
 * **Dictation is the phone's own keyboard mic** (§12, binding decision B3/D22), so there is
 * nothing to build for it: this is a plain `<textarea>` and the phone keyboard's microphone
 * writes into it. No audio file is ever created, and the label §12 standardises on — *Type Or
 * Dictate* — is the field's own.
 *
 * **The write is idempotent on a client-generated id**, so a save pressed twice on a bad
 * connection is one note. The id comes from `newNoteId`, which the UM capture path already uses
 * for exactly the same reason.
 *
 * **Step 01 is narrowed by role as of run 52.** The tournament runs 57 officials, and 57 names
 * in one `<select>` is a spinning wheel on a phone at pitch-side — the surface this card exists
 * for. So a Role select sits ABOVE the Official one and the Official list shows only that role.
 * The Role select opens on a placeholder rather than on "All Roles", because a default that
 * shows everyone gives the TD the long list back on every fresh note and the shortening would
 * only ever happen when he remembered to ask for it. Choosing the role is one tap, and it is
 * the tap that makes the second field usable.
 *
 * **Changing the role clears the official, and therefore the category.** The Official select is
 * filtered, not disabled, so a subject held over from the previous role would be a selection the
 * TD can no longer see — a note filed against someone who is not on screen. The typed body is
 * NOT cleared: the role and the official are how the note is addressed, and the words are the
 * note itself.
 */

import { useState } from "react";

import {
  type OfficialRole,
  TD_NOTE_GENERAL_LABEL,
  tdNoteCategoriesForRole,
  tidyCriterionDisplayName,
} from "@hockeytd/shared";

import { fetchOfficials, newNoteId, putTdNote } from "../../api/client.js";
import { useAsync } from "../../app/useAsync.js";
import { FailBanner, Skeleton } from "../../app/states.js";

/**
 * The select's value for General. Empty string rather than a word, so the option that means
 * "no criterion" is the same empty value the Official select uses for "nobody chosen" and the
 * write turns it into the null the store holds — there is no third spelling anywhere.
 */
const GENERAL = "";

/**
 * The order the Role select lists roles in, written out rather than taken from the roster's own
 * order, so that a roster which happens to arrive sorted some other way — or a role added to the
 * enum in a later migration — cannot silently re-order the first field the TD touches. It is the
 * same seniority order the roster screen and `ROLE_ORDER_SQL` already use. Roles with nobody in
 * this tournament are not offered: an empty choice is a wasted tap.
 */
const ROLE_ORDER: readonly OfficialRole[] = [
  "TD",
  "ATD",
  "TO",
  "Judge",
  "UM",
  "Umpire",
  "Tournament Administrator",
];

export function TdQuickNoteCapture(): React.JSX.Element {
  const roster = useAsync(() => fetchOfficials(), []);
  const [role, setRole] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(GENERAL);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [failure, setFailure] = useState<Error | null>(null);

  const officials = roster.status === "ready" ? roster.data.officials : [];
  // Only the roles somebody actually holds, in the fixed order above.
  const roles = ROLE_ORDER.filter((candidate) =>
    officials.some((official) => official.role === candidate),
  );
  // The roster's own order is preserved within the role — this filters, it does not re-sort.
  const shown = role === "" ? [] : officials.filter((official) => official.role === role);
  const chosen = officials.find((official) => official.tmsPersonId === subject);
  // No official chosen yet means no set to offer: General is the only honest option until the
  // card knows whose criteria these would be.
  const categories = chosen === undefined ? [] : tdNoteCategoriesForRole(chosen.role);
  const ready = subject !== "" && body.trim() !== "" && !saving;

  const save = (): void => {
    setSaving(true);
    setSaid(null);
    setFailure(null);
    putTdNote(newNoteId(), {
      subjectTmsPersonId: subject,
      body: body.trim(),
      // General is the absence of a criterion, so it goes as null rather than as a word the
      // store would then have to know two spellings of. See the header.
      criterionStableKey: category === GENERAL ? null : category,
    }).then(
      () => {
        setSaving(false);
        setBody("");
        // Named rather than counted: the confirmation says WHERE the note went, because the
        // panel that shows it is on another screen and the TD is about to walk away from this
        // one. "Saved" alone would leave him wondering whether it had gone to the UM stream.
        setSaid("Filed to this official's Quick Notes on their profile.");
      },
      (error: unknown) => {
        setSaving(false);
        setFailure(error instanceof Error ? error : new Error("The note could not be saved."));
      },
    );
  };

  return (
    <section className="panel panel--d3" aria-labelledby="tdCaptureTitle" data-testid="td-capture">
      <div className="panel__head">
        <h3 className="panel__title" id="tdCaptureTitle">
          Quick Note
        </h3>
        <span className="spacer" />
        <span className="panel__note">Your Own Stream · Read On The Official&rsquo;s Profile</span>
      </div>

      {roster.status === "loading" ? <Skeleton rows={2} /> : null}
      {roster.status === "error" ? <FailBanner error={roster.error} /> : null}

      {roster.status === "ready" ? (
        <div className="tdcapture">
          <label className="tdcapture__role">
            Role
            <select
              value={role}
              disabled={saving}
              data-testid="td-capture-role"
              onChange={(event) => {
                setRole(event.target.value);
                // The official list is about to change under the TD's hand: anyone held over
                // would be a subject he can no longer see. Clear it, and the category with it —
                // the sets are role-specific. The body is his words and stays. See the header.
                setSubject("");
                setCategory(GENERAL);
                setSaid(null);
              }}
            >
              <option value="">Select a role…</option>
              {roles.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {candidate}
                </option>
              ))}
            </select>
            <span className="field__sub">
              Pick the role first — the list of officials below then shows only that role.
            </span>
          </label>

          <label>
            Official
            <select
              value={subject}
              disabled={saving}
              data-testid="td-capture-official"
              onChange={(event) => {
                setSubject(event.target.value);
                // A different official is a different marking set — see the header.
                setCategory(GENERAL);
                setSaid(null);
              }}
            >
              <option value="">
                {role === "" ? "Select a role first…" : "Select an official…"}
              </option>
              {shown.map((official) => (
                <option key={official.tmsPersonId} value={official.tmsPersonId}>
                  {official.mediaName} · {official.role} · {official.country}
                </option>
              ))}
            </select>
          </label>

          <label>
            Category
            <select
              value={category}
              disabled={saving}
              data-testid="td-capture-category"
              onChange={(event) => {
                setCategory(event.target.value);
                setSaid(null);
              }}
            >
              <option value={GENERAL}>{TD_NOTE_GENERAL_LABEL}</option>
              {categories.map((criterion) => (
                <option
                  key={criterion.criterion_stable_key}
                  value={criterion.criterion_stable_key}
                >
                  {tidyCriterionDisplayName(criterion.name)}
                </option>
              ))}
            </select>
            <span className="field__sub">
              A criterion files the note beside that mark. {TD_NOTE_GENERAL_LABEL} notes are the
              ones the draft tournament report summary reads.
            </span>
          </label>

          <label className="tdcapture__body">
            Type Or Dictate
            <textarea
              value={body}
              disabled={saving}
              data-testid="td-capture-body"
              placeholder="What you saw, in your own words."
              onChange={(event) => {
                setBody(event.target.value);
                setSaid(null);
              }}
            />
          </label>

          <div className="tdcapture__actions">
            <button
              type="button"
              className="linkbtn"
              disabled={!ready}
              data-testid="td-capture-save"
              onClick={save}
            >
              {saving ? "Saving…" : "Save To Log"}
            </button>
            {said === null ? null : (
              <span className="tdcapture__said" role="status">
                {said}
              </span>
            )}
            {failure === null ? null : <FailBanner error={failure} />}
          </div>
        </div>
      ) : null}

      <div className="panel__foot">
        <b>Your own stream, and only yours.</b> These notes are the Technical Delegate&rsquo;s
        record of technical officials. They are kept in a separate store from the Umpires
        Managers&rsquo; notes, never mix with them, are read on the official&rsquo;s own profile,
        and appear on no outbound or printed document. Dictation is your phone
        keyboard&rsquo;s own microphone — no audio file is created or stored.
      </div>
    </section>
  );
}
